
import React, { useState } from 'react';
import { Container, Typography, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SkillLabel from '../Common/SkillLabel';
import TranslatedText from '../Common/TranslatedText';
import { mediaVariantUrl, mediaSrcSet } from '../../utils/mediaUrl';

const COLORS = {
  sectionBg:      '#FAF8F5',
  cardBg:         '#FFFFFF',
  border:         '#E8DDD4',
  heading:        '#2D2D2D',
  eyebrow:        '#9A6845',
  name:           '#1A1A1A',
  tagline:        '#C97B3A',
  desc:           '#888888',
  iconBg:         '#FFFFFF',
  iconColor:      '#C97B3A',
  imageBg:        '#F0E8DF',
  shadow:         'rgba(0,0,0,0.08)',
  shadowHover:    'rgba(0,0,0,0.16)',
  scrollbar:      '#D8C7B8',
  scrollbarHover: '#9A6845',
  chipBg:         '#FAF0E8',
  chipText:       '#9A6845',
  chipBorder:     '#E8DDD4',
};

const CARD_W        = 200;   // wide card
const CARD_PADDING  = 10;    // gap between card edge and image (the "frame")
const IMG_H_DESKTOP = 200;   // tall image
const IMG_H_MOBILE  = 155;
const ICON_D        = 38;

/**
 * Featured-service image that lazy-loads below the fold and fades in over an
 * icon placeholder, so cards never show a blank image area while downloading.
 */
const FeaturedImage = ({ src, alt, IconComponent }) => {
  const [loaded, setLoaded] = useState(false);
  const displaySrc = mediaVariantUrl(src, 'medium');
  const srcSet = mediaSrcSet(src);

  return (
    <>
      {(!loaded || !src) && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {IconComponent && (
            <IconComponent sx={{ fontSize: { xs: 36, md: 42 }, color: COLORS.iconColor }} />
          )}
        </Box>
      )}
      {src && (
        <Box
          component="img"
          src={displaySrc}
          srcSet={srcSet}
          sizes="(max-width: 600px) 155px, (max-width: 900px) 175px, 200px"
          alt={alt}
          width={320}
          height={440}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(false)}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.35s ease',
          }}
        />
      )}
    </>
  );
};

const FeaturedServicesSection = ({ featuredServices, services = [], location }) => {
  const navigate = useNavigate();

  const iconMap = React.useMemo(() => {
    const map = {};
    services.forEach((s) => {
      if (s?.name && s?.icon) map[s.name.toLowerCase()] = s.icon;
    });
    return map;
  }, [services]);

  const handleCardClick = (serviceName) => {
    const params = new URLSearchParams();
    if ((location || '').trim()) params.set('location', location.trim());
    if (serviceName) params.set('service', serviceName);
    navigate(`/search?${params.toString()}`);
  };

  if (!featuredServices || featuredServices.length === 0) return null;

  return (
    <Box sx={{ pt: { xs: 1, md: 1.5 }, pb: { xs: 4, md: 5 }, bgcolor: COLORS.sectionBg }}>
      <Container maxWidth="lg">

        {/* Eyebrow */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 0.75 }}>
          <Box sx={{ flex: 1, maxWidth: 100, height: '1px', bgcolor: COLORS.border }} />
          <Typography sx={{
            fontSize: { xs: '0.6rem', md: '0.65rem' },
            fontWeight: 600,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: COLORS.eyebrow,
            whiteSpace: 'nowrap',
          }}>
            Explore Our Services
          </Typography>
          <Box sx={{ flex: 1, maxWidth: 100, height: '1px', bgcolor: COLORS.border }} />
        </Box>

        {/* Heading */}
        <Typography variant="h4" sx={{
          fontWeight: 700,
          color: COLORS.heading,
          textAlign: 'center',
          fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.9rem' },
          letterSpacing: '-0.01em',
          mb: { xs: 2.5, md: 3 },
        }}>
          Featured Services
        </Typography>

        {/* Cards row — horizontal scroll on all screen sizes */}
        <Box sx={{
          display:        'flex',
          flexDirection:  'row',
          alignItems:     'stretch',
          overflowX:      'auto',
          gap:            { xs: '12px', md: '16px' },
          pb:             1,
          '&::-webkit-scrollbar':       { height: 4 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: COLORS.scrollbar,
            borderRadius: 2,
            '&:hover': { background: COLORS.scrollbarHover },
          },
        }}>
          {featuredServices.map((service, index) => {
            const IconComponent = iconMap[service.name?.toLowerCase()] || null;

            return (
              <Box
                key={service.name || index}
                onClick={() => handleCardClick(service.name)}
                sx={{
                  cursor:    'pointer',
                  flexShrink: 0,
                  width:     { xs: 155, sm: 175, md: CARD_W },
                  display:   'flex',
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    '& .fs-card': { boxShadow: `0 10px 28px ${COLORS.shadowHover}` },
                  },
                }}
              >
                {/* ── White card — padding creates the frame around image ── */}
                <Box
                  className="fs-card"
                  sx={{
                    bgcolor:        COLORS.cardBg,
                    borderRadius:   '16px',
                    border:         `1px solid ${COLORS.border}`,
                    boxShadow:      `0 3px 12px ${COLORS.shadow}`,
                    transition:     'box-shadow 0.2s ease',
                    width:          '100%',
                    display:        'flex',
                    flexDirection:  'column',
                    alignItems:     'center',
                    px:             `${CARD_PADDING}px`,
                    pt:             `${CARD_PADDING}px`,
                    pb:             '14px',
                    overflow:       'visible',
                    height:         '100%',
                    boxSizing:      'border-box',
                  }}
                >
                  {/* ── Image — rounded, sits inside the white frame ── */}
                  <Box sx={{
                    position:     'relative',
                    width:        '100%',
                    height:       { xs: IMG_H_MOBILE, sm: 170, md: IMG_H_DESKTOP },
                    borderRadius: '10px',
                    overflow:     'hidden',
                    flexShrink:   0,
                    border:       `1px solid ${COLORS.border}`,
                    bgcolor:      COLORS.imageBg,
                  }}>
                    <FeaturedImage
                      src={service.image}
                      alt={service.name}
                      IconComponent={IconComponent}
                    />
                  </Box>

                  {/* ── Icon badge — overlaps image bottom ── */}
                  <Box sx={{
                    width:          { xs: ICON_D - 2, md: ICON_D },
                    height:         { xs: ICON_D - 2, md: ICON_D },
                    borderRadius:   '50%',
                    bgcolor:        COLORS.iconBg,
                    border:         `1.5px solid ${COLORS.border}`,
                    boxShadow:      `0 2px 8px ${COLORS.shadow}`,
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'center',
                    mt:             { xs: `-${Math.round((ICON_D - 2) / 2)}px`, md: `-${ICON_D / 2}px` },
                    mb:             '8px',
                    zIndex:         1,
                    flexShrink:     0,
                    position:       'relative',
                  }}>
                    {IconComponent ? (
                      <IconComponent sx={{ fontSize: { xs: 14, md: 16 }, color: COLORS.iconColor }} />
                    ) : (
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: COLORS.iconColor, opacity: 0.4 }} />
                    )}
                  </Box>

                  {/* ── Text block ── */}
                  <Box sx={{ textAlign: 'center', width: '100%', mb: '10px', flexGrow: 1 }}>

                    <Typography
                      component="div"
                      className="notranslate"
                      translate="no"
                      sx={{
                        fontWeight:    800,
                        fontSize:      { xs: '0.65rem', md: '0.75rem' },
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color:         COLORS.name,
                        lineHeight:    1.2,
                        mb:            '4px',
                      }}
                    >
                      <SkillLabel name={service.name} />
                    </Typography>

                    {service.title && (
                      <Typography sx={{
                        fontWeight:         600,
                        fontSize:           { xs: '0.6rem', md: '0.68rem' },
                        color:              COLORS.tagline,
                        lineHeight:         1.3,
                        mb:                 '4px',
                        display:            '-webkit-box',
                        WebkitLineClamp:    1,
                        WebkitBoxOrient:    'vertical',
                        overflow:           'hidden',
                      }}>
                        {service.title}
                      </Typography>
                    )}

                    {service.description && (
                      <Typography sx={{
                        fontSize:          { xs: '0.55rem', md: '0.62rem' },
                        color:             COLORS.desc,
                        lineHeight:        1.5,
                        display:           '-webkit-box',
                        WebkitLineClamp:   2,
                        WebkitBoxOrient:   'vertical',
                        overflow:          'hidden',
                      }}>
                        {service.description}
                      </Typography>
                    )}
                  </Box>

                  {/* ── Book Now button ── */}
                  <Box
                    onClick={(e) => { e.stopPropagation(); handleCardClick(service.name); }}
                    sx={{
                      mt:             'auto',
                      width:          '100%',
                      display:        'flex',
                      alignItems:     'center',
                      justifyContent: 'center',
                      gap:            '5px',
                      bgcolor:        COLORS.tagline,
                      color:          '#FFFFFF',
                      borderRadius:   '10px',
                      px:             '12px',
                      py:             '7px',
                      fontSize:       { xs: '0.58rem', md: '0.65rem' },
                      fontWeight:     700,
                      letterSpacing:  '0.06em',
                      textTransform:  'uppercase',
                      whiteSpace:     'nowrap',
                      cursor:         'pointer',
                      transition:     'background 0.18s ease, transform 0.15s ease',
                      '&:hover': {
                        bgcolor:   '#B56A2E',
                        transform: 'scale(1.03)',
                      },
                    }}
                  >
                    <TranslatedText>Book Now</TranslatedText>
                  </Box>

                </Box>
              </Box>
            );
          })}
        </Box>

      </Container>
    </Box>
  );
};

export default React.memo(FeaturedServicesSection);